import { z } from "zod";
import { ColumnSchema, ViewSchema, ViewType } from "./views";

export enum ClientsCloseOverviewColumn {
    CLIENT = "client",
    ENTITIES = "entities",
    TEAM = "team",
    ACCESS = "access",
    PLAN = "plan",
    BILLING_OWNERSHIP = "billingOwnership",
    LEGAL_TYPE = "legalType",
    NOTE = "note",
    SERVICES = "services",
    BOOKKEEPING_START_DATE = "bookkeepingStartDate",
    CREATED_AT = "createdAt",
    ACTIONS = "actions",
}

// Close-level and entity-level column ids that the clients overview no longer
// renders. They stay parseable so persisted views still referencing them keep
// validating instead of being discarded; the view config omits them, so the
// table ignores them.
type RemovedCloseOverviewColumnId =
    | "closeStart"
    | "closeEnd"
    | "closeStage"
    | "timeInStage"
    | "preparerReviewer"
    | "transactionCount";

const makeColumnSchema = (
    id: ClientsCloseOverviewColumn | RemovedCloseOverviewColumnId,
) => ColumnSchema.extend({ id: z.literal(id) });

export const ClientsCloseOverviewViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.CLIENTS_CLOSE_OVERVIEW),
    columns: z.array(
        z.discriminatedUnion("id", [
            makeColumnSchema(ClientsCloseOverviewColumn.CLIENT),
            makeColumnSchema(ClientsCloseOverviewColumn.ENTITIES),
            makeColumnSchema(ClientsCloseOverviewColumn.TEAM),
            makeColumnSchema(ClientsCloseOverviewColumn.ACCESS),
            makeColumnSchema(ClientsCloseOverviewColumn.PLAN),
            makeColumnSchema(ClientsCloseOverviewColumn.BILLING_OWNERSHIP),
            makeColumnSchema(ClientsCloseOverviewColumn.LEGAL_TYPE),
            makeColumnSchema(ClientsCloseOverviewColumn.NOTE),
            makeColumnSchema(ClientsCloseOverviewColumn.SERVICES),
            makeColumnSchema(ClientsCloseOverviewColumn.BOOKKEEPING_START_DATE),
            makeColumnSchema(ClientsCloseOverviewColumn.CREATED_AT),
            makeColumnSchema(ClientsCloseOverviewColumn.ACTIONS),
            makeColumnSchema("closeStart"),
            makeColumnSchema("closeEnd"),
            makeColumnSchema("closeStage"),
            makeColumnSchema("timeInStage"),
            makeColumnSchema("preparerReviewer"),
            makeColumnSchema("transactionCount"),
        ]),
    ),
});

export type ClientsCloseOverviewView = z.infer<
    typeof ClientsCloseOverviewViewSchema
>;
